// src/pages/AtivacoesExcluidasLogAgg.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, Select, MenuItem,
  Button, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Divider, LinearProgress, Tooltip, Chip, InputAdornment,
  Collapse, IconButton
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ComputerIcon from "@mui/icons-material/Computer";
import LinkIcon from "@mui/icons-material/Link";
import TodayIcon from "@mui/icons-material/Today";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import api from "../services/api";
import { Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const toLicenca    = (chave)       => `/licencas/novo/${encodeURIComponent(chave)}`;

const toRevendedor = async (chaveUsuario, idFromRow) => {
  try {
    const id =
      idFromRow ||
      // reaproveita sua rota de busca existente
      (await (async () => {
        const { data } = await api.get('/usuarios-busca', { params: { q: chaveUsuario } });
        const achado = Array.isArray(data) ? data.find(u => u.chave === chaveUsuario) : null;
        return achado?.id;
      })());

    if (!id) {
      alert('Revendedor não encontrado.');
      return;
    }
    window.open(`/usuarios/novo/${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
  } catch (e) {
    console.error('Falha ao abrir revendedor:', e);
    alert('Não foi possível abrir o revendedor.');
  }
};

function toCSV(rows, cols) {
  const header = cols.map(c => `"${c.label.replace(/"/g, '""')}"`).join(";");
  const lines = rows.map(r =>
    cols.map(c => {
      const v = r[c.key] ?? "";
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(";")
  );
  return [header, ...lines].join("\r\n");
}
function downloadBlob(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AtivacoesExcluidasLogAgg() {
  const [dim, setDim] = useState("pc");            // pc | user | pc_user
  const [minCount, setMinCount] = useState(0);
  const [dtIni, setDtIni] = useState("");          // yyyy-mm-dd
  const [dtFim, setDtFim] = useState("");          // yyyy-mm-dd
  const [chaveLicenca, setChaveLicenca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // cache de detalhes por grupo
  const [expanded, setExpanded] = useState({});       // {key: bool}
  const [detailsCache, setDetailsCache] = useState({});// {key: {loading, rows, error}}

  // colunas variam pela dimensão
  const cols = useMemo(() => {
    if (dim === "pc") {
      return [
        { key: "id_computador", label: "ID Computador" },
        { key: "qtd", label: "Qtd" },
      ];
    }
    if (dim === "user") {
      return [
        { key: "chave_usuario", label: "Chave Usuário" },
        { key: "qtd", label: "Qtd" },
      ];
    }
    return [
      { key: "id_computador", label: "ID Computador" },
      { key: "chave_usuario", label: "Chave Usuário" },
      { key: "qtd", label: "Qtd" },
    ];
  }, [dim]);

  const buildParams = useCallback(() => {
    const params = {
      dim,
      minCount: Number.isFinite(minCount) ? minCount : 0,
      page: page + 1,          // API é 1-based
      pageSize: rowsPerPage,
      _: Date.now(),           // anti-cache
    };
    if (dtIni) params.dt_ini = dtIni;
    if (dtFim) params.dt_fim = dtFim;
    if ((chaveLicenca || "").trim()) params.chave_licenca = chaveLicenca.trim();
    return params;
  }, [dim, minCount, dtIni, dtFim, chaveLicenca, page, rowsPerPage]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/ativacao_CSHAP/ativacoes-excluidas-log/agg", { params: buildParams() });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.error || e?.message || "Erro ao carregar";
      console.error("agg load error:", status, e?.response?.data || e);
      setErr(`Erro ${status || ""} — ${message}`);
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { setPage(0); setExpanded({}); setDetailsCache({}); }, [dim, minCount, dtIni, dtFim, chaveLicenca]);
  useEffect(() => { load(); }, [load]);

  const handleClear = () => {
    setDim("pc"); setMinCount(0); setDtIni(""); setDtFim(""); setChaveLicenca("");
    setPage(0); setRowsPerPage(50);
  };

  const handleExportCSV = () => {
    const csv = toCSV(rows, cols);
    const nameDim = dim === "pc" ? "por-computador" : dim === "user" ? "por-usuario" : "pc-usuario";
    downloadBlob(csv, `ativacoes-excluidas_${nameDim}.csv`);
  };

  const groupKey = (g) => `${g.id_computador || ''}__${g.chave_usuario || ''}`;

  const fetchDetails = async (group) => {
    const key = groupKey(group);
    setDetailsCache(prev => ({ ...prev, [key]: { loading: true, rows: [], error: "" } }));
    try {
      const params = { page: 1, pageSize: 200 };
      if (dtIni) params.dt_ini = dtIni;
      if (dtFim) params.dt_fim = dtFim;
      if (chaveLicenca.trim()) params.chave_licenca = chaveLicenca.trim();
      if (group.id_computador) params.id_computador = group.id_computador;
      if (group.chave_usuario) params.chave_usuario = group.chave_usuario;

      const { data } = await api.get("/ativacao_CSHAP/ativacoes-excluidas-log", { params });
      const list = Array.isArray(data?.rows) ? data.rows : [];
      setDetailsCache(prev => ({ ...prev, [key]: { loading: false, rows: list, error: "" } }));
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.error || e?.message || "Erro ao carregar detalhes";
      setDetailsCache(prev => ({ ...prev, [key]: { loading: false, rows: [], error: `Erro ${status || ""} — ${message}` } }));
    }
  };

  const toggleExpand = async (group) => {
    const key = groupKey(group);
    const willOpen = !expanded[key];
    setExpanded(prev => ({ ...prev, [key]: willOpen }));
    if (willOpen && !detailsCache[key]) {
      await fetchDetails(group);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h6" fontWeight={800}>Logs de Ativações Excluídas (Agregado)</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-end">
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FilterAltIcon fontSize="inherit" /> Dimensão
            </Typography>
            <Select fullWidth size="small" value={dim} onChange={e => setDim(e.target.value)}>
              <MenuItem value="pc">
                <Stack direction="row" alignItems="center" gap={1}>
                  <ComputerIcon fontSize="small" /> Por Computador
                </Stack>
              </MenuItem>
              <MenuItem value="user">
                <Stack direction="row" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" /> Por Usuário
                </Stack>
              </MenuItem>
              <MenuItem value="pc_user">
                <Stack direction="row" alignItems="center" gap={1}>
                  <ComputerIcon fontSize="small" />
                  <PersonIcon fontSize="small" />
                  PC + Usuário
                </Stack>
              </MenuItem>
            </Select>
          </Box>

          <Box sx={{ minWidth: 160 }}>
            <Typography variant="caption">Pelo menos (N)</Typography>
            <TextField
              type="number" size="small" fullWidth inputProps={{ min: 0 }}
              value={minCount}
              onChange={e => setMinCount(Math.max(0, Number(e.target.value)))}
              InputProps={{ startAdornment: <InputAdornment position="start">≥</InputAdornment> }}
            />
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TodayIcon fontSize="inherit" /> Data Início
            </Typography>
            <TextField type="date" size="small" fullWidth value={dtIni} onChange={e => setDtIni(e.target.value)} />
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TodayIcon fontSize="inherit" /> Data Fim
            </Typography>
            <TextField type="date" size="small" fullWidth value={dtFim} onChange={e => setDtFim(e.target.value)} />
          </Box>

          <Box sx={{ minWidth: 280, flex: 1 }}>
            <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LinkIcon fontSize="inherit" /> Chave da Licença (opcional)
            </Typography>
            <TextField size="small" fullWidth value={chaveLicenca} onChange={e => setChaveLicenca(e.target.value)} placeholder="Ex.: LIC-ABC-123" />
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Buscar (atualizar)">
              <span>
                <Button variant="contained" onClick={load} disabled={loading} startIcon={<SearchIcon />}>
                  {loading ? "Carregando..." : "Buscar"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Limpar filtros">
              <Button variant="outlined" color="inherit" onClick={handleClear} startIcon={<RestartAltIcon />}>
                Limpar
              </Button>
            </Tooltip>
            <Tooltip title="Exportar CSV (tela atual)">
              <span>
                <Button variant="outlined" color="success" startIcon={<DownloadIcon />} disabled={rows.length === 0} onClick={handleExportCSV}>
                  CSV
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Chip label={`Dimensão: ${dim}`} size="small" />
          <Chip label={`≥ ${minCount}`} size="small" />
          {dtIni && <Chip label={`De: ${dtIni}`} size="small" />}
          {dtFim && <Chip label={`Até: ${dtFim}`} size="small" />}
          {(chaveLicenca || "").trim() && <Chip label={`Licença: ${(chaveLicenca || "").trim()}`} size="small" />}
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">{total} grupo(s)</Typography>
        </Stack>
      </Paper>

      <Paper sx={{ position: "relative" }}>
        {loading && <Box sx={{ position: "absolute", left: 0, right: 0, top: 0 }}><LinearProgress /></Box>}

        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50", width: 48 }} />
              {cols.map(c => (
                <TableCell key={c.key} sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {err ? err : "Sem resultados"}
                </TableCell>
              </TableRow>
            )}

            {rows.map((r, i) => {
              const key = groupKey(r);
              const isOpen = !!expanded[key];
              const det = detailsCache[key];

              return (
                <React.Fragment key={[r.id_computador || "", r.chave_usuario || "", r.qtd || "", i].join("-")}>
                  <TableRow
                    hover
                    sx={{ bgcolor: r.qtd > 1 ? "rgba(255, 193, 7, 0.12)" : undefined }}
                  >
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(r)} aria-label="expandir">
                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>

                    {cols.map(c => {
                      let val = r[c.key];
                      if (val === null || val === undefined || val === "") val = "—";
                      const isQtd = c.key === "qtd";
                      return (
                        <TableCell key={c.key} sx={isQtd && r.qtd > 1 ? { fontWeight: 700 } : undefined}>
                          {val}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={cols.length + 1} sx={{ p: 0, borderBottom: 0 }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                          {(!det || det.loading) && <LinearProgress sx={{ mb: 1 }} />}
                          {det && det.error && (
                            <Typography color="error" sx={{ mb: 1 }}>{det.error}</Typography>
                          )}

                          {det && !det.loading && !det.error && (
                            <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Data Cadastro</TableCell>
                                  <TableCell>ID Ativação</TableCell>
                                  <TableCell>Chave Licença</TableCell>
                                  <TableCell>Chave Usuário</TableCell>
                                  <TableCell>ID Computador</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {det.rows?.length ? det.rows.map((d) => (
                                  <TableRow key={d.id}>
                                    <TableCell>{d.id}</TableCell>
                                    <TableCell>{d.data_cadastro}</TableCell>
                                    <TableCell>{d.id_ativacao}</TableCell>

                                    <TableCell>
                                      {d.chave_licenca ? (
                                        <Link
                                          component={RouterLink}
                                          to={toLicenca(d.chave_licenca)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                          onClick={(e)=>e.stopPropagation()}
                                          onMouseDown={(e)=>e.stopPropagation()}
                                          sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600 }}
                                        >
                                          {d.chave_licenca}
                                        </Link>
                                      ) : "—"}
                                    </TableCell>

                                    <TableCell>
                                      {d.chave_usuario ? (
                                        <Link
                                          component="button"
                                          underline="hover"
                                          onClick={(e) => { e.stopPropagation(); toRevendedor(d.chave_usuario, d.usuario_id); }}
                                          sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
                                        >
                                          {d.chave_usuario}
                                        </Link>
                                      ) : "—"}
                                    </TableCell>

                                    <TableCell>{d.id_computador}</TableCell>
                                  </TableRow>
                                )) : (
                                  <TableRow>
                                    <TableCell colSpan={6} align="center">Sem linhas</TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
}
